import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ChildrenOutletContexts, NavigationEnd, PRIMARY_OUTLET, Router } from '@angular/router';
import { SharedService } from '@ace/ace';
import { TranslationService } from '@ace/ace';
import { filter } from 'rxjs';
import { PageRefreshService } from './services/page-refresh.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  menu;
  title = 'ABG';
  isLoading: boolean = false;
  private unlistenSideMenuClick?: () => void;
  constructor(
    private renderer: Renderer2,
    private translationService: TranslationService,
    private sharedService: SharedService,
    private router: Router,
    private outletContexts: ChildrenOutletContexts,
    private pageRefreshService: PageRefreshService,
  ) 
  {
  }

   ngOnInit() {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(async (e: NavigationEnd) => {
        const url = e.urlAfterRedirects;
        if (url === '/login') {
          this.renderer.setAttribute(document.documentElement, 'dir', 'ltr');
        } else {
          const saved = localStorage.getItem('appLang') || 'en';
          this.renderer.setAttribute(
            document.documentElement,
            'dir',
            saved === 'ar' ? 'rtl' : 'ltr'
          );
          await this.translationService.init(saved);
        }
      });
    const initialUrl = this.router.url;
    if (initialUrl === '/login') {
      this.renderer.setAttribute(document.documentElement, 'dir', 'ltr');
    } else {
      const saved = localStorage.getItem('appLang') || 'en';
      this.renderer.setAttribute(
        document.documentElement,
        'dir',
        saved === 'ar' ? 'rtl' : 'ltr'
      );
      this.translationService.init(saved);
    }

    this.loadTheme();
    this.sharedService.isLoading.subscribe(l => (this.isLoading = l));
    this.bindSideMenuRefresh();
  }

  ngOnDestroy(): void {
    this.unlistenSideMenuClick?.();
  }

  private bindSideMenuRefresh(): void {
    const handleSideMenuClick = (event: MouseEvent) => {
      if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
        return;
      }

      const target = this.getEventElement(event.target);
      const sidebarContainer = target?.closest('.app-sidebar-menu, #sidebar-menu');

      if (!sidebarContainer) {
        return;
      }

      const currentUrl = `${window.location.pathname}${window.location.search}`;
      const sideMenuLink = target?.closest('a, button, [role="button"], [routerlink], [ng-reflect-router-link]');

      if (!sideMenuLink || !this.isSideMenuContentClick(event, target, sideMenuLink)) {
        return;
      }

      const href = sideMenuLink?.getAttribute('href')?.trim();
      const routerLink = (
        sideMenuLink?.getAttribute('routerlink') ||
        sideMenuLink?.getAttribute('ng-reflect-router-link')
      )?.trim();
      const destination = href || routerLink;

      if (destination?.startsWith('#') || destination?.startsWith('javascript:')) {
        return;
      }

      if (destination) {
        const resolvedUrl = new URL(destination, window.location.origin);
        const clickedUrl = `${resolvedUrl.pathname}${resolvedUrl.search}`;

        if (clickedUrl !== currentUrl) {
          return;
        }

        setTimeout(() => this.refreshActivePage(currentUrl), 0);
        return;
      }

      if (sideMenuLink.hasAttribute('aria-expanded')) {
        return;
      }

      setTimeout(() => {
        const settledUrl = `${window.location.pathname}${window.location.search}`;
        if (settledUrl === currentUrl) {
          this.refreshActivePage(currentUrl);
        }
      }, 0);
    };

    document.addEventListener('click', handleSideMenuClick, true);
    this.unlistenSideMenuClick = () => {
      document.removeEventListener('click', handleSideMenuClick, true);
    };
  }

  private getEventElement(target: EventTarget | null): Element | null {
    if (target instanceof Element) {
      return target;
    }

    if (target instanceof Text && target.parentElement) {
      return target.parentElement;
    }

    return null;
  }

  private isSideMenuContentClick(event: MouseEvent, target: Element, sideMenuLink: Element): boolean {
    const menuArrow = target.closest('.menu-arrow');
    if (menuArrow && sideMenuLink.contains(menuArrow)) {
      return false;
    }

    if (target === sideMenuLink) {
      return true;
    }

    const contentTarget = target.closest(
      '.menu-fontawesome-icon, .menu-lucide-icon, .menu-fallback-icon, .menu-icon, .nav-icon, .menu-name, .menu-text, .nav-link-text, span, i, svg',
    );

    if (contentTarget && sideMenuLink.contains(contentTarget)) {
      return true;
    }

    return this.isPointInsideSideMenuContent(event, sideMenuLink);
  }

  private isPointInsideSideMenuContent(event: MouseEvent, sideMenuLink: Element): boolean {
    const contentElements = sideMenuLink.querySelectorAll(
      '.menu-fontawesome-icon, .menu-lucide-icon, .menu-fallback-icon, .menu-icon, .nav-icon, .menu-name, .menu-text, .nav-link-text, span, i, svg',
    );

    for (const element of Array.from(contentElements)) {
      if (element.closest('.menu-arrow')) {
        continue;
      }

      const rect = element.getBoundingClientRect();
      if (
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom
      ) {
        return true;
      }
    }

    return false;
  }

  private refreshActivePage(expectedUrl: string): void {
    const activeUrl = `${window.location.pathname}${window.location.search}`;
    if (activeUrl !== expectedUrl) {
      return;
    }

    const component = this.getDeepestActivatedComponent();
    if (!component) {
      return;
    }

    if (typeof component.reloadPageData === 'function') {
      void component.reloadPageData(true);
      return;
    }

    this.pageRefreshService.requestRefresh(activeUrl);

    if (typeof component.ngOnDestroy === 'function') {
      component.ngOnDestroy();
    }

    if (typeof component.ngOnInit === 'function') {
      component.ngOnInit();
    }
  }

  private getDeepestActivatedComponent(): any {
    let contexts: ChildrenOutletContexts | null = this.outletContexts;
    let activeComponent: any = null;

    while (contexts) {
      const context = contexts.getContext(PRIMARY_OUTLET);
      if (!context) {
        break;
      }

      if (context.outlet?.isActivated) {
        activeComponent = context.outlet.component;
      }

      contexts = context.children;
    }

    return activeComponent;
  }


  loadTheme() {
    let theme = localStorage.getItem("selectedTheme")
    if (theme == "theme1") {
      /** Light Theme **/
      const sourceColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-light');
      const sourceColor_1 = getComputedStyle(document.documentElement).getPropertyValue('--primary-light-1');
      const sourceColor_2 = getComputedStyle(document.documentElement).getPropertyValue('--primary-light-2');
      const whiteColor = getComputedStyle(document.documentElement).getPropertyValue('--white-color');
      const lightGreyColor = getComputedStyle(document.documentElement).getPropertyValue('--light-grey-color');
      const darkRedColor = getComputedStyle(document.documentElement).getPropertyValue('--dark-red');
      const blackColor = getComputedStyle(document.documentElement).getPropertyValue('--black-color');
      /* Main */
      document.documentElement.style.setProperty('--default-color', sourceColor);
      document.documentElement.style.setProperty('--default-color-1', sourceColor_1);
      document.documentElement.style.setProperty('--default-color-2', sourceColor_2);
      /* Grid */
      document.documentElement.style.setProperty('--grid-icon-color', sourceColor);
      document.documentElement.style.setProperty('--grid-bg-color', whiteColor);
      document.documentElement.style.setProperty('--grid-text-color', lightGreyColor);
      document.documentElement.style.setProperty('--grid-del-icon', darkRedColor);
      document.documentElement.style.setProperty('--grid-view-icon', sourceColor);
      document.documentElement.style.setProperty('--grid-edit-icon', sourceColor);
      document.documentElement.style.setProperty('--grid-download-icon', sourceColor);
      document.documentElement.style.setProperty('--grid-column-name-color', blackColor);
      /* Sidebar */
      document.documentElement.style.setProperty('--sidebar-bg-color', whiteColor);
      document.documentElement.style.setProperty('--sidebar-icon-color', lightGreyColor);
      document.documentElement.style.setProperty('--sidebar-selected-icon-color', sourceColor);
      /* Box Color */
      document.documentElement.style.setProperty('--box-bg-color', whiteColor);
      document.documentElement.style.setProperty('--box-text-color', blackColor);
    } else if (theme == "theme2") {
      /** Dark Theme **/
      const sourceColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark');
      const sourceColor_1 = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark-1');
      const sourceColor_2 = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark-2');
      const whiteColor = getComputedStyle(document.documentElement).getPropertyValue('--white-color');
      const lightGreyColor = getComputedStyle(document.documentElement).getPropertyValue('--light-grey-color');
      /* Main */
      document.documentElement.style.setProperty('--default-color', sourceColor);
      document.documentElement.style.setProperty('--default-color-1', sourceColor_1);
      document.documentElement.style.setProperty('--default-color-2', sourceColor_2);
      /* Grid */
      document.documentElement.style.setProperty('--grid-icon-color', whiteColor);
      document.documentElement.style.setProperty('--grid-bg-color', sourceColor);
      document.documentElement.style.setProperty('--grid-text-color', whiteColor);
      document.documentElement.style.setProperty('--grid-del-icon', whiteColor);
      document.documentElement.style.setProperty('--grid-view-icon', whiteColor);
      document.documentElement.style.setProperty('--grid-edit-icon', whiteColor);
      document.documentElement.style.setProperty('--grid-download-icon', whiteColor);
      document.documentElement.style.setProperty('--grid-column-name-color', whiteColor);
      /* Sidebar */
      document.documentElement.style.setProperty('--sidebar-bg-color', sourceColor);
      document.documentElement.style.setProperty('--sidebar-icon-color', lightGreyColor);
      document.documentElement.style.setProperty('--sidebar-selected-icon-color', whiteColor);
      /* Box Color */
      document.documentElement.style.setProperty('--box-bg-color', sourceColor);
      document.documentElement.style.setProperty('--box-text-color', whiteColor);
    }
  }
}
